import {
  listTagsinaproject,
  ListTagsinaprojectData,
  ListTagsinaprojectResponse,
  createTag,
  CreateTagData,
  CreateTagResponse,
  deleteTag,
  DeleteTagData,
  DeleteTagResponse,
  editTag,
  EditTagData,
  EditTagResponse,
  removeobjectsfromTag,
  RemoveobjectsfromTagData,
  RemoveobjectsfromTagResponse,
  addobjectstoTag,
  AddobjectstoTagData,
  AddobjectstoTagResponse,
} from '@data-access/connect/tc-tcps';
import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';
import { CACHE_TIMES } from '../constants/cache-times';

// Types for batch tag operations
export interface BatchTagRequest {
  method: 'GET';
  actions: BatchTagAction[];
}

export interface BatchTagAction {
  queryParams: {
    projectId: string;
    objectId: string;
    objectType: 'FOLDER' | 'FILE' | 'ISSUE';
  };
}

export interface BatchTagResponse {
  method: 'GET';
  actions: BatchTagActionResponse[];
}

export interface BatchTagActionResponse {
  responseBody: TagResponse[];
  tcRequestId: string;
  queryParams: {
    projectId: string;
    objectId: string;
    objectType: string;
  };
}

export interface TagResponse {
  id: string;
  projectId: string;
  label: string;
  description?: string;
  color?: string;
  createdOn: string;
  createdBy?: string;
  updatedOn?: string;
  updatedBy?: string;
}

export class TagApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient,
    private readonly getBatchApiDomain?: () => string,
    private readonly accessToken?: string
  ) {}

  // Query for listing tags in a project
  public getTagsQuery(
    options?: Accessor<Omit<ListTagsinaprojectData, 'client'> | undefined>
  ): UseQueryResult<ListTagsinaprojectResponse, Error> {
    return useQuery(() => ({
      queryKey: ['project-tags', options?.()],
      queryFn: async (): Promise<ListTagsinaprojectResponse> => {
        const response = await listTagsinaproject({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...options?.(),
        });

        if (response.error) {
          throw new Error('Failed to list tags');
        }

        return response.data!;
      },
      enabled: true,
    }));
  }

  // Query for getting tags for multiple objects in batch
  public getTagsBatchQuery(
    actions?: Accessor<BatchTagAction[] | undefined>
  ): UseQueryResult<BatchTagResponse, Error> {
    return useQuery(() => ({
      queryKey: ['batch-tags', actions?.()],
      queryFn: async (): Promise<BatchTagResponse> => {
        const batchActions = actions?.();

        if (!batchActions || batchActions.length === 0) {
          throw new Error('No actions provided for batch tag request');
        }

        const requestBody: BatchTagRequest = {
          method: 'GET',
          actions: batchActions,
        };

        try {
          // Make direct fetch call to batch API endpoint
          const headers: Record<string, string> = {};
          headers['Content-Type'] = 'application/json';

          // Add authorization header using access token
          if (this.accessToken) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
          }

          // Use configured batch API domain or fallback to production
          const batchApiBaseUrl =
            this.getBatchApiDomain?.() ||
            'https://batch-api.connect.trimble.com/v1';

          const response = await fetch(
            `${batchApiBaseUrl}/bulk-requests/TC-TAGS/TAG`,
            {
              method: 'POST',
              headers,
              body: JSON.stringify(requestBody),
            }
          );

          if (!response.ok) {
            throw new Error(
              `Failed to fetch batch tags: ${String(response.status)} ${response.statusText}`
            );
          }

          const data: BatchTagResponse =
            (await response.json()) as BatchTagResponse;
          return data;
        } catch (error) {
          console.error('Error in batch tags request:', error);
          throw new Error(
            error instanceof Error
              ? error.message
              : 'Failed to fetch batch tags'
          );
        }
      },
      enabled: () => {
        const batchActions = actions?.();
        return Boolean(batchActions && batchActions.length > 0);
      },
      staleTime: CACHE_TIMES.STALE_TIME,
      cacheTime: CACHE_TIMES.CACHE_TIME,
    }));
  }

  // Utility method to create batch actions for multiple objects
  public static createBatchActions(
    requests: Array<{
      projectId: string;
      objectId: string;
      objectType: 'FOLDER' | 'FILE' | 'ISSUE';
    }>
  ): BatchTagAction[] {
    return requests.map((request) => ({
      queryParams: {
        projectId: request.projectId,
        objectId: request.objectId,
        objectType: request.objectType,
      },
    }));
  }

  // Utility method to extract tags by object from batch response
  public static extractTagsByObject(
    batchResponse: BatchTagResponse
  ): Map<string, TagResponse[]> {
    const tagsByObject = new Map<string, TagResponse[]>();

    batchResponse.actions.forEach((action) => {
      const key = `${action.queryParams.projectId}-${action.queryParams.objectId}-${action.queryParams.objectType}`;
      tagsByObject.set(key, action.responseBody);
    });

    return tagsByObject;
  }

  // Mutation for creating a new tag
  public createTagMutation(): UseMutationResult<
    CreateTagResponse,
    Error,
    CreateTagData
  > {
    return useMutation(() => ({
      mutationFn: async (data: CreateTagData): Promise<CreateTagResponse> => {
        const response = await createTag({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to create tag');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate tags list query
        void this.queryClient.invalidateQueries({
          queryKey: ['project-tags'],
        });
      },
    }));
  }

  // Mutation for editing a tag
  public editTagMutation(): UseMutationResult<
    EditTagResponse,
    Error,
    EditTagData
  > {
    return useMutation(() => ({
      mutationFn: async (data: EditTagData): Promise<EditTagResponse> => {
        const response = await editTag({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to edit tag');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate tags list query
        void this.queryClient.invalidateQueries({
          queryKey: ['project-tags'],
        });
      },
    }));
  }

  // Mutation for deleting a tag
  public deleteTagMutation(): UseMutationResult<
    DeleteTagResponse,
    Error,
    DeleteTagData
  > {
    return useMutation(() => ({
      mutationFn: async (data: DeleteTagData): Promise<DeleteTagResponse> => {
        const response = await deleteTag({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to delete tag');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate tags list query
        void this.queryClient.invalidateQueries({
          queryKey: ['project-tags'],
        });
      },
    }));
  }

  // Mutation for adding objects to a tag
  public addObjectsToTagMutation(): UseMutationResult<
    AddobjectstoTagResponse,
    Error,
    AddobjectstoTagData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: AddobjectstoTagData
      ): Promise<AddobjectstoTagResponse> => {
        const response = await addobjectstoTag({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to add objects to tag');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate tags list and specific tag objects
        void this.queryClient.invalidateQueries({
          queryKey: ['project-tags'],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['tag-objects', variables.path.tagId],
        });
      },
    }));
  }

  // Mutation for removing objects from a tag
  public removeObjectsFromTagMutation(): UseMutationResult<
    RemoveobjectsfromTagResponse,
    Error,
    RemoveobjectsfromTagData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: RemoveobjectsfromTagData
      ): Promise<RemoveobjectsfromTagResponse> => {
        const response = await removeobjectsfromTag({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to remove objects from tag');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate tags list and specific tag objects
        void this.queryClient.invalidateQueries({
          queryKey: ['project-tags'],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['tag-objects', variables.path.tagId],
        });
      },
    }));
  }
}
